import { useMemo } from 'react';
import { buildCountyMap } from '../utils/buildCountyMap';

/**
 * Helper function to check if well is inactive (matches RelationalChart logic)
 */
const isInactiveWell = (well) => {
  const plugDate = well['Plug Date'] || well.plug_date || well.plugged_date || well.plugDate || null;
  const wellGroup = well['Well Group'] || well.well_group || well.well_status || well.status || well.wellGroup || '';
  const inactiveYears = well.inactive_years !== undefined ? well.inactive_years : (well.inactiveYears !== undefined ? well.inactiveYears : 0);
  const wellGroupUpper = wellGroup.toString().toUpperCase();
  
  const isUnplugged = plugDate === null || plugDate === undefined || plugDate === '';
  const isActiveOrPermit = wellGroupUpper === 'ACTIVE' || wellGroupUpper === 'PERMIT';
  
  return (isUnplugged && !isActiveOrPermit) || (isActiveOrPermit && inactiveYears > 0);
};

/**
 * Calculate red flags for a lease (matches RelationalChart logic)
 */
const calculateRedFlags = (lease, leaseKey, searchResults) => {
  const flags = {
    inactiveWells: false,
    inactiveWellsOver5Years: false,
    flaringIntensity: false,
    fuelGasIntensity: false,
    inspectionsCompliance: false,
    violationsCount: false,
    violationsMajor: false,
    violationsReinspection: false,
    severancesCount: false,
    severancesResolved: false,
    spillsCount: false,
    spillsWaterImpact: false
  };
  
  // Wells flags
  if (lease.wells && lease.wells.length > 0) {
    const inactiveWells = lease.wells.filter(well => isInactiveWell(well));
    flags.inactiveWells = inactiveWells.length > 0;
    
    const inactiveOver5Years = inactiveWells.filter(well => {
      const inactiveYears = well.inactiveYears !== undefined ? well.inactiveYears : (well.inactive_years || 0);
      return inactiveYears > 5;
    });
    flags.inactiveWellsOver5Years = inactiveOver5Years.length > 0;
    
    let wellFlagsCount = 0;
    inactiveWells.forEach(well => {
      wellFlagsCount += 1;
      const inactiveYears = well.inactiveYears !== undefined ? well.inactiveYears : (well.inactive_years || 0);
      if (inactiveYears > 5) {
        wellFlagsCount += 1;
      }
    });
    flags._wellFlagsCount = wellFlagsCount;
  }
  
  // Production flags
  if (lease.production && lease.production.avgFlaringIntensity !== undefined) {
    flags.flaringIntensity = lease.production.avgFlaringIntensity > 2;
  }
  
  if (lease.production && lease.production.avgFuelGasIntensity !== undefined) {
    flags.fuelGasIntensity = lease.production.avgFuelGasIntensity > 5;
  }
  
  // Inspections flags
  if (lease.inspections && lease.inspections.complianceRate !== undefined) {
    flags.inspectionsCompliance = lease.inspections.complianceRate < 90;
  }
  
  // Violations flags
  if (lease.violations && lease.violations.count !== undefined) {
    flags.violationsCount = lease.violations.count > 0;
  }
  
  if (lease.violations && lease.violations.majorCount !== undefined) {
    flags.violationsMajor = lease.violations.majorCount > 0;
  }
  
  if (lease.violations && lease.violations.reinspectionComplianceRate !== undefined) {
    flags.violationsReinspection = lease.violations.reinspectionComplianceRate < 100;
  }
  
  // Severances flags
  if (lease.severances && lease.severances.count !== undefined) {
    flags.severancesCount = lease.severances.count > 0;
  }
  
  if (lease.severances && lease.severances.resolvedRate !== undefined) {
    flags.severancesResolved = lease.severances.resolvedRate < 100;
  }
  
  // Spills flags
  if (lease.spills && lease.spills.count !== undefined) {
    flags.spillsCount = lease.spills.count > 0;
  }
  
  if (lease.spills && lease.spills.waterImpactCount !== undefined) {
    flags.spillsWaterImpact = lease.spills.waterImpactCount > 0;
  }
  
  // Calculate total red flags
  let totalFlags = 0;
  
  if (flags._wellFlagsCount !== undefined) {
    totalFlags += flags._wellFlagsCount;
  } else {
    if (flags.inactiveWells) totalFlags += 1;
    if (flags.inactiveWellsOver5Years) totalFlags += 1;
  }
  
  if (flags.flaringIntensity) totalFlags += 1;
  if (flags.fuelGasIntensity) totalFlags += 1;
  if (flags.inspectionsCompliance) totalFlags += 1;
  if (flags.violationsCount) totalFlags += 1;
  if (flags.violationsMajor) totalFlags += 1;
  if (flags.violationsReinspection) totalFlags += 1;
  if (flags.severancesCount) totalFlags += 1;
  if (flags.severancesResolved) totalFlags += 1;
  if (flags.spillsCount) totalFlags += 1;
  if (flags.spillsWaterImpact) totalFlags += 1;
  
  return { flags, totalFlags };
};

/**
 * Custom hook to calculate flag maps for leases and counties
 * @param {Object} searchResults - Search results data
 * @param {Object} searchParams - Search parameters (for includedTables)
 * @param {Map|null} countyMap - Optional pre-built countyMap (if provided, uses it; otherwise builds using buildCountyMap)
 * @returns {Object} { leaseFlagsMap, countyFlagsMap, redFlagsCache }
 */
export const useFlagMaps = (searchResults, searchParams, countyMap = null) => {
  // Determine included tables
  const includedTables = useMemo(() => {
    if (!searchParams) {
      return { wells: true, production: true, inspections: true, violations: true, severances: true, spills: true };
    }
    
    return {
      wells: searchParams.search_wells || false,
      production: searchParams.search_production || false,
      inspections: searchParams.search_inspections || false,
      violations: searchParams.search_violations || false,
      severances: searchParams.search_severances || false,
      spills: searchParams.search_spills || false,
    };
  }, [searchParams]);

  // Build or use provided countyMap
  const effectiveCountyMap = useMemo(() => {
    if (countyMap) {
      return countyMap; // Use provided countyMap
    }
    
    // Build countyMap using shared utility
    const { countyMap: builtCountyMap } = buildCountyMap(searchResults, includedTables);
    return builtCountyMap;
  }, [countyMap, searchResults, includedTables]);

  // Memoize red flags for all leases - store per county-lease combination to avoid overwriting
  const redFlagsCache = useMemo(() => {
    if (!effectiveCountyMap || !searchResults) return new Map();
    const cache = new Map();
    effectiveCountyMap.forEach((leases, county) => {
      leases.forEach((leaseData, leaseKey) => {
        const { flags, totalFlags } = calculateRedFlags(leaseData, leaseKey, searchResults);
        // Store with composite key: county-leaseKey to avoid overwriting when lease spans counties
        const countyLeaseKey = `${county}-${leaseKey}`;
        cache.set(countyLeaseKey, { flags, totalFlags, county, leaseKey });
      });
    });
    return cache;
  }, [effectiveCountyMap, searchResults]);

  // Calculate flag maps for map display
  const flagMaps = useMemo(() => {
    if (!effectiveCountyMap || !searchResults) {
      return { leaseFlagsMap: new Map(), countyFlagsMap: new Map() };
    }

    const leaseFlagsMap = new Map();
    const countyFlagsMap = new Map();

    effectiveCountyMap.forEach((leases, county) => {
      let countyTotalFlags = 0;
      leases.forEach((leaseData, leaseKey) => {
        // Calculate flags per county-lease combination (don't use overwritten cache)
        const { totalFlags } = calculateRedFlags(leaseData, leaseKey, searchResults);
        const countyLeaseKey = `${county}-${leaseKey}`;
        
        // Store county-lease specific flags for map markers and lease cards
        leaseFlagsMap.set(countyLeaseKey, totalFlags);
        
        // Sum flags per county for county aggregation (this is the fix!)
        countyTotalFlags += totalFlags;
      });
      if (countyTotalFlags > 0) {
        countyFlagsMap.set(county.toUpperCase(), countyTotalFlags);
      }
    });

    return { leaseFlagsMap, countyFlagsMap };
  }, [effectiveCountyMap, searchResults]);

  return { 
    leaseFlagsMap: flagMaps.leaseFlagsMap, 
    countyFlagsMap: flagMaps.countyFlagsMap,
    redFlagsCache
  };
};

